import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  AppBar, Toolbar, Typography, Button, Box, 
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText 
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu'; 

// --- IMPORT ICONS ---
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArticleIcon from '@mui/icons-material/Article';       
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'; 
import EventIcon from '@mui/icons-material/Event';           
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'; 
// --- NEW IMPORT ---
import SearchIcon from '@mui/icons-material/Search'; 
// ------------------

import logo from './assets/app-logo.png'; 

function Navbar() {
  // --- AUTOMATIC YEAR VARIABLE ---
  const currentYear = new Date().getFullYear();
  // -------------------------------

  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));

  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsLoggedIn(!!localStorage.getItem('token'));
    };
    window.addEventListener('storageChanged', handleStorageChange);
    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storageChanged', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.dispatchEvent(new Event('storageChanged')); 
    navigate('/login');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogoutAndClose = () => {
    handleLogout();
    handleMenuClose();
  }

  // --- MOBILE HAMBURGER MENU CONTENT ---
  const renderMobileMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      keepMounted
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      {/* --- NEW SEARCH ITEM (MOBILE) --- */}
      <MenuItem onClick={() => handleMenuClick('/dashboard')}>
        <ListItemIcon><SearchIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Search</ListItemText>
      </MenuItem>
      {/* -------------------------------- */}

      <MenuItem onClick={() => handleMenuClick('/blog')}>
        <ListItemIcon><ArticleIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Blog</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleMenuClick('/games')}>
        <ListItemIcon><SportsEsportsIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Games</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleMenuClick('/info')}>
        <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Events & Info</ListItemText>
      </MenuItem>
      <MenuItem onClick={() => handleMenuClick('/privacy')}>
        <ListItemIcon><PrivacyTipIcon fontSize="small" /></ListItemIcon>
        <ListItemText>Privacy Policy</ListItemText>
      </MenuItem>
      
      {isLoggedIn && [
          <Box key="divider" sx={{ borderTop: '1px solid #eee', my: 1 }} />,
          <MenuItem key="subs" onClick={() => handleMenuClick('/my-submissions')}>
            <ListItemIcon><ListAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>My Submissions</ListItemText>
          </MenuItem>,
          <MenuItem key="add" onClick={() => handleMenuClick('/add')}>
             <ListItemIcon><AddLocationIcon fontSize="small" /></ListItemIcon>
             <ListItemText>Add Display</ListItemText>
          </MenuItem>,
          <MenuItem key="logout" onClick={handleLogoutAndClose}>
             <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
             <ListItemText>Logout</ListItemText>
          </MenuItem>
      ]}
    </Menu>
  );

  return (
    <AppBar position="static">
      <Toolbar sx={{ minHeight: { xs: 72 } }}> 
        
        <Box
          component="img"
          src={logo}
          alt="Christmas Lights Map Logo"
          sx={{ height: 40, mr: 2 }}
        />

        <Typography 
          variant="h6" 
          component={RouterLink} 
          to="/" 
          sx={{ flexGrow: 1, color: 'inherit', textDecoration: 'none', fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
          Christmas Lights Map {currentYear}
        </Typography>

        {/* --- MOBILE LAYOUT (Visible < 900px) --- */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          
          {!isLoggedIn && (
            <>
              <Button 
                color="inherit" 
                size="small" 
                component={RouterLink} 
                to="/login"
                startIcon={<LoginIcon />} 
                sx={{ minWidth: 'auto', mr: 0.5, textTransform: 'none' }}
              >
                Login
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="small" 
                component={RouterLink} 
                to="/register"
                startIcon={<PersonAddIcon />} 
                sx={{ 
                  minWidth: 'auto', 
                  mr: 1, 
                  textTransform: 'none', 
                  borderColor: 'rgba(255,255,255,0.5)' 
                }}
              >
                Join
              </Button>
            </>
          )}

          <IconButton
            size="large"
            edge="end"
            color="inherit"
            aria-label="open menu"
            onClick={handleMenuOpen}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* --- DESKTOP LAYOUT (Visible >= 900px) --- */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          
          {/* --- NEW SEARCH BUTTON (DESKTOP) --- */}
          <Button color="inherit" component={RouterLink} to="/dashboard" startIcon={<SearchIcon />}>
            Search
          </Button>
          {/* ----------------------------------- */}

          <Button color="inherit" component={RouterLink} to="/blog" startIcon={<ArticleIcon />}>
            Blog
          </Button>
          <Button color="inherit" component={RouterLink} to="/games" startIcon={<SportsEsportsIcon />}>
            Games
          </Button>
          <Button color="inherit" component={RouterLink} to="/info" startIcon={<EventIcon />}>
            Events & Info
          </Button>
          <Button color="inherit" component={RouterLink} to="/privacy" startIcon={<PrivacyTipIcon />}>
            Privacy Policy
          </Button>

          {isLoggedIn ? (
            <>
              <Button color="inherit" component={RouterLink} to="/my-submissions" startIcon={<ListAltIcon />}>
                My Submissions
              </Button>
              <Button color="inherit" component={RouterLink} to="/add" startIcon={<AddLocationIcon />}>
                Add Display
              </Button>
              <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
                Logout
              </Button>
            </>
          ) : (
            <>
              <Button color="inherit" component={RouterLink} to="/login" startIcon={<LoginIcon />}>
                Login
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                component={RouterLink} 
                to="/register"
                startIcon={<PersonAddIcon />}
                sx={{ ml: 1, borderColor: 'rgba(255,255,255,0.5)' }}
              >
                Register
              </Button>
            </>
          )}
        </Box>

      </Toolbar>
      {renderMobileMenu}
    </AppBar>
  );
}

export default Navbar;